import { styled } from '@mui/material/styles';
import { Box, Typography, Chip } from '@mui/material';
import { FunctionField, RecordContextProvider, useDataProvider, useTranslate } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import { PromotionalCampaignsTabs } from '@/components/promotionalCampaigns/PromotionalCampaignsTabs.jsx';
import { daysDiff } from '@/helpers/date/dateHelpers';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const StyledPromotionalCampaignsDetailsContent = styled(Box)({
    padding: '26px 56px 56px 78px',
    '& .top-details-box': {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        '& .MuiBox-root': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
        },
    },
    '& .MuiTypography-root.MuiTypography-body1': {
        letterSpacing: '-0.14px',
    },
    '& .MuiTypography-root.MuiTypography-caption': {
        fontSize: '9px',
        fontWeight: 700,
        lineHeight: '12px',
        letterSpacing: '0.36px',
        textTransform: 'uppercase',
        width: '56px',
    },
    '& .MuiChip-root': {
        padding: '6px',
        borderRadius: '12px',
        '& .MuiChip-label': {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '-0.14px',
            paddingLeft: '0px',
            paddingRight: '0px',
        },
    },
});

export const PromotionalCampaignsDetailsContent = ({ elementId }) => {
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const resource = 'promotional-campaigns';

    const {
        data: campaign,
        isPending,
        error,
    } = useQuery({
        queryKey: [resource, elementId],
        queryFn: () => dataProvider.getOne(resource, { id: Number.parseInt(elementId, 10) }),
        enabled: !!elementId,
        select: response => response.data,
        onError: err => {
            console.error('Error fetching promotional campaign:', err);
        },
    });

    const loadingState = getLoadingState({ isPending, error, data: campaign, translate });
    if (loadingState) return loadingState;

    return (
        <RecordContextProvider value={campaign}>
            <StyledPromotionalCampaignsDetailsContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '38px' }}>
                    <FunctionField
                        source='period'
                        render={record => (
                            <Box className='top-details-box'>
                                <Typography variant='caption'>{translate('resources.campaign.labels.schedule')}</Typography>
                                <Typography variant='body1'>
                                    {record.startDate ? format(record.startDate, 'dd.MM', { locale: pl }) : '—'} -{' '}
                                    {record.endDate ? format(record.endDate, 'dd.MM.yyyy', { locale: pl }) : '—'} ({daysDiff(record.endDate, record.startDate)}{' '}
                                    dni)
                                </Typography>
                            </Box>
                        )}
                    />

                    <FunctionField
                        source='criteria'
                        render={record => (
                            <Box className='top-details-box'>
                                <Typography variant='caption'>{translate('resources.campaign.labels.criteria')}</Typography>
                                <Box>
                                    {record.criteria?.map(criterion => (
                                        <Chip key={criterion} label={criterion} size='medium' />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    />

                    <FunctionField
                        source='coupon'
                        render={record => (
                            <Box className='top-details-box'>
                                <Typography variant='caption'>{translate('resources.campaign.labels.coupon')}</Typography>
                                <Box>
                                    {record.coupon?.map(coupon => (
                                        <Chip key={coupon} label={coupon} size='medium' />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    />
                </Box>

                <PromotionalCampaignsTabs conversionData={campaign.conversionData} viewsData={campaign.viewsData} />
            </StyledPromotionalCampaignsDetailsContent>
        </RecordContextProvider>
    );
};
